import { lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Utility } from '../helpers/utility';
import {
  ClientObservabilityJobOutcome,
  IClientObservabilityIngestion,
  IClientObservabilityJobsPage,
  IClientObservabilityPipeline
} from '../models/IClientObservability';
import { IAISummaryResponse } from '../models/IAISummary';
import {
  IClientObservabilityApiOperation,
  ObservabilityFlagFamily
} from '../models/IClientObservabilityApiOperation';
import { IObservabilitySignals } from '../models/IObservabilitySignals';

export class ClientObservabilityApis
  implements IClientObservabilityApiOperation
{
  private constructor(
    private readonly httpClient: HttpClient,
    private readonly apiV1: string
  ) {}

  public static clientObservabilityApiOperation(
    httpClient: HttpClient,
    apiV1: string
  ): IClientObservabilityApiOperation {
    return new ClientObservabilityApis(httpClient, apiV1);
  }

  public getObservabilitySignals(
    clientId: number,
    flagFamily: ObservabilityFlagFamily,
    summaryOnly = false,
    includeDrivers = false,
    dateStart?: string,
    dateStop?: string
  ): Promise<IObservabilitySignals> {
    return this.get<IObservabilitySignals>('/bff/observability/signals', {
      clientId,
      flagFamily,
      summaryOnly,
      includeDrivers,
      dateStart,
      dateStop
    });
  }

  public getIngestionHealth(
    clientId: number,
    lookbackDays?: number
  ): Promise<IClientObservabilityIngestion> {
    return this.get<IClientObservabilityIngestion>(
      '/bff/client-observability/ingestion',
      {
        clientId,
        lookbackDays
      }
    );
  }

  public getPipelineHealth(
    clientId: number,
    lookbackDays?: number
  ): Promise<IClientObservabilityPipeline> {
    return this.get<IClientObservabilityPipeline>(
      '/bff/client-observability/pipeline',
      {
        clientId,
        lookbackDays
      }
    );
  }

  public getJobs(
    clientId: number,
    outcome: ClientObservabilityJobOutcome,
    lookbackDays?: number,
    page = 1,
    pageSize = 25
  ): Promise<IClientObservabilityJobsPage> {
    return this.get<IClientObservabilityJobsPage>(
      '/bff/client-observability/jobs',
      {
        clientId,
        outcome,
        lookbackDays,
        page,
        pageSize
      }
    );
  }

  public getSpecSummary(
    clientId: number,
    specType: 'ccmaf' | 'transaction',
    specId: number
  ): Promise<IAISummaryResponse> {
    return this.get<IAISummaryResponse>(
      '/bff/client-observability/spec-summary',
      {
        clientId,
        specType,
        specId
      }
    );
  }

  private get<T>(
    path: string,
    queryParameters: Record<string, string | number | boolean | undefined>
  ): Promise<T> {
    const url = Utility.buildURL({
      url: `${this.apiV1}${path}`,
      queryParameters
    });
    return lastValueFrom(this.httpClient.get<T>(url));
  }
}
